/**
 * The parts of a call that need no browser: what to do with a candidate that
 * arrives early, who has left, and how to read a peer's state. Kept apart
 * from the hook so they can be tested without an RTCPeerConnection.
 */

import type { Call, UUID } from '@/api/types'

/**
 * ICE candidates race the answer they belong to. Chrome throws when one is
 * added before the remote description is set, and the first version of this
 * client let that rejection vanish unhandled — the candidate with it. Hold
 * them until the description lands, then drain in order.
 */
export class PendingCandidates {
  private readonly held = new Map<UUID, RTCIceCandidateInit[]>()

  /** Queue a candidate for a peer whose remote description is not set yet. */
  hold(userId: UUID, candidate: RTCIceCandidateInit): void {
    const list = this.held.get(userId) ?? []
    list.push(candidate)
    this.held.set(userId, list)
  }

  /** Everything held for the peer, in arrival order, and forgotten here. */
  drain(userId: UUID): RTCIceCandidateInit[] {
    const list = this.held.get(userId) ?? []
    this.held.delete(userId)
    return list
  }

  forget(userId: UUID): void {
    this.held.delete(userId)
  }

  size(userId: UUID): number {
    return this.held.get(userId)?.length ?? 0
  }
}

/** Who is in the call besides you, according to the server. */
export function joinedPeerIds(call: Call, selfId: UUID | undefined): UUID[] {
  return call.participants
    .filter((participant) => participant.status === 'joined')
    .map((participant) => participant.user?.id)
    .filter((id): id is UUID => typeof id === 'string' && id !== selfId)
}

/**
 * Peers we hold a connection to who are no longer joined. Their connection
 * is closed and their card removed; until this existed a departed
 * participant stayed on screen, frozen, for the rest of the call.
 */
export function departedPeerIds(connected: Iterable<UUID>, call: Call, selfId: UUID | undefined): UUID[] {
  const stillIn = new Set(joinedPeerIds(call, selfId))
  return [...connected].filter((id) => !stillIn.has(id))
}

export type PeerState = 'connecting' | 'connected' | 'reconnecting' | 'failed'

/**
 * One word per peer. `connected` is the only state in which media is known to
 * be flowing; the channel having joined says nothing about that, which is
 * why "Connected" used to appear over a call with no sound in it.
 */
export function describePeer(state: RTCPeerConnectionState | undefined): PeerState {
  switch (state) {
    case 'connected':
      return 'connected'
    case 'disconnected':
      return 'reconnecting'
    case 'failed':
    case 'closed':
      return 'failed'
    default:
      return 'connecting'
  }
}

/** What the header says about the call as a whole. */
export function describeCall(peers: Record<UUID, RTCPeerConnectionState>, channelJoined: boolean): string {
  const states = Object.values(peers).map(describePeer)
  if (!channelJoined) return 'Connecting…'
  if (states.length === 0) return 'Waiting for the others to join'
  if (states.every((state) => state === 'connected')) return 'Connected'
  if (states.some((state) => state === 'failed')) return 'Could not reach everyone'
  if (states.some((state) => state === 'reconnecting')) return 'Reconnecting…'
  return 'Connecting…'
}
